import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { statusEntry, toneStyle } from '@/components/vf/statusMap';
import BookedMeetingForm from '@/components/dialer/BookedMeetingForm';
import { copyText } from '@/components/shared/CopyLink';
import type { CustomerCrmView } from '@/services/dialer/bookedMeeting';
import { PAD } from '@/services/dialer/callPage/outcomes';
import { COMMON_TIME_ZONES } from '@/services/dialer/callPage/zonedTime';
import type { PadOutcome } from '@/api/endpoints/qualifications';
import type { CallSessionView } from '@/hooks/useCallSessionView';

export interface Draft {
  outcome: PadOutcome | null;
  source: 'human' | 'ai' | null;
  note: string;
  followDate: string;
  followTime: string;
  timezone: string;
  meetingDone: boolean;
  showMeetingForm: boolean;
  /** CEO-12: the AI suggestion, kept even after a human changes the pad. */
  suggested: PadOutcome | null;
}

export interface PaneError { code: string; correlationId: string }

export interface QualificationPaneHandle { focusNote(): void; focusPad(): void }

interface Props {
  view: CallSessionView;
  callName: string;
  prospectId: string | null;
  parallelCallId: string | null;
  customer: CustomerCrmView | null;
  draft: Draft;
  draftStatus: 'idle' | 'saved' | 'failed';
  saving: boolean;
  error: PaneError | null;
  enabled: boolean;
  onDraft(patch: Partial<Draft>): void;
  onSelect(outcome: PadOutcome): void;
  onSave(stayPaused: boolean): void;
  /** QA-W7-2: why saving is off (calls.qualify removed from the role), '' or absent when allowed. */
  saveBlockedReason?: string;
  onMeetingDone(): void;
  onUndo(): void;
  onHold(): void;
  onNext(): void;
}

/**
 * Spec §4.5 / DES-3 / DES-4: notes, then the outcome pad (a radiogroup:
 * arrows move, 1–8 pick, Entrée saves), then what the outcome needs (the
 * meeting form, the callback date). Nothing is saved before the hang-up;
 * the countdown starts only once everything is saved.
 */
export const QualificationPane = forwardRef<QualificationPaneHandle, Props>(function QualificationPane(p, ref) {
  const { t } = useTranslation(['dialer']);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const padRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => ({
    focusNote: () => noteRef.current?.focus(),
    focusPad: () => {
      const target = padRef.current?.querySelector<HTMLElement>('[aria-checked="true"]') ?? padRef.current?.querySelector<HTMLElement>('[role="radio"]');
      target?.focus();
    },
  }));

  const qualifying = p.view.phase === 'qualifying';
  const countdown = p.view.countdown;
  const selected = p.draft.outcome;

  const onPadKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(e.key)) return;
    e.preventDefault();
    const i = Math.max(0, PAD.findIndex((x) => x.outcome === selected));
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
    const next = PAD[(i + step + PAD.length) % PAD.length]!;
    p.onSelect(next.outcome);
    padRef.current?.querySelector<HTMLElement>(`[data-outcome="${next.outcome}"]`)?.focus();
  };

  return (
    <section
      aria-label={t('dialer:callPage.notes.label')}
      className="flex w-[420px] shrink-0 flex-col gap-4 overflow-y-auto border-l p-4 max-[1279px]:w-[340px]"
      style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-surface-1)' }}
    >
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label htmlFor="call-note" className="text-sm font-medium" style={{ color: 'var(--vf-text-1)' }}>
            {p.callName ? t('dialer:callPage.notes.for', { name: p.callName }) : t('dialer:callPage.notes.label')}
          </label>
          {p.draftStatus !== 'idle' && (
            <span className="text-[11px]" style={{ color: p.draftStatus === 'failed' ? 'var(--vf-red)' : 'var(--vf-text-3)' }}>
              {t(`dialer:callPage.notes.draft_${p.draftStatus}`)}
            </span>
          )}
        </div>
        <Textarea
          id="call-note"
          ref={noteRef}
          value={p.draft.note}
          disabled={!p.enabled}
          onChange={(e) => p.onDraft({ note: e.target.value })}
          placeholder={t('dialer:callPage.notes.placeholder')}
          className="min-h-28 text-sm"
        />
      </div>

      <div>
        <h2 id="call-pad-label" className="mb-2 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--vf-text-2)' }}>
          {t('dialer:callPage.pad.label')}
        </h2>
        <div
          ref={padRef}
          role="radiogroup"
          data-call-pad
          aria-labelledby="call-pad-label"
          onKeyDown={onPadKey}
          className="grid grid-cols-2 gap-2"
        >
          {PAD.map((entry) => {
            const isSel = selected === entry.outcome;
            const suggested = isSel && p.draft.source === 'ai';
            const style = toneStyle(statusEntry(entry.outcome).tone);
            return (
              <button
                key={entry.outcome}
                type="button"
                role="radio"
                data-outcome={entry.outcome}
                aria-checked={isSel}
                tabIndex={isSel || (!selected && entry.key === '1') ? 0 : -1}
                disabled={!p.enabled}
                onClick={() => p.onSelect(entry.outcome)}
                className="flex min-h-11 items-center gap-2 rounded-lg border px-2.5 text-left text-sm focus-visible:outline focus-visible:outline-2 disabled:opacity-50"
                style={{
                  borderColor: isSel ? style.fg : 'var(--vf-border)',
                  borderStyle: suggested ? 'dashed' : 'solid',
                  background: isSel && !suggested ? style.bg : 'transparent',
                  color: 'var(--vf-text-1)',
                  outlineColor: 'var(--vf-accent)',
                }}
              >
                <kbd className="rounded border px-1 font-mono text-[11px]" style={{ borderColor: 'var(--vf-border)', color: 'var(--vf-text-2)' }}>{entry.key}</kbd>
                <span className="flex-1">{t(`dialer:callPage.outcomes.${entry.labelKey}`)}</span>
                {suggested && <span className="text-[10px] font-medium" style={{ color: style.fg }}>{t('dialer:callPage.pad.suggested')}</span>}
              </button>
            );
          })}
        </div>
        {!qualifying && selected && p.view.phase === 'connected' && (
          <p className="mt-2 text-xs" style={{ color: 'var(--vf-text-3)' }}>{t('dialer:callPage.pad.preselected')}</p>
        )}
      </div>

      {selected === 'callback_requested' && (
        <fieldset className="grid grid-cols-3 gap-2">
          <legend className="mb-1 text-xs" style={{ color: 'var(--vf-text-2)' }}>{t('dialer:callPage.callback.required')}</legend>
          <label className="text-xs" style={{ color: 'var(--vf-text-2)' }}>
            {t('dialer:callPage.callback.date')}
            <input type="date" required value={p.draft.followDate} onChange={(e) => p.onDraft({ followDate: e.target.value })}
              className="mt-1 h-11 w-full rounded-md border bg-transparent px-2 text-sm" style={{ borderColor: 'var(--vf-border)', color: 'var(--vf-text-1)' }} />
          </label>
          <label className="text-xs" style={{ color: 'var(--vf-text-2)' }}>
            {t('dialer:callPage.callback.time')}
            <input type="time" required value={p.draft.followTime} onChange={(e) => p.onDraft({ followTime: e.target.value })}
              className="mt-1 h-11 w-full rounded-md border bg-transparent px-2 text-sm" style={{ borderColor: 'var(--vf-border)', color: 'var(--vf-text-1)' }} />
          </label>
          <label className="text-xs" style={{ color: 'var(--vf-text-2)' }}>
            {t('dialer:callPage.callback.timezone')}
            <select value={p.draft.timezone} onChange={(e) => p.onDraft({ timezone: e.target.value })}
              className="mt-1 h-11 w-full rounded-md border bg-transparent px-1 text-sm" style={{ borderColor: 'var(--vf-border)', color: 'var(--vf-text-1)' }}>
              {[...new Set([p.draft.timezone, ...COMMON_TIME_ZONES])].map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </label>
        </fieldset>
      )}

      {selected === 'meeting_booked' && p.draft.showMeetingForm && !p.draft.meetingDone && (
        <div className="rounded-lg border p-2" style={{ borderColor: 'var(--vf-border)' }}>
          <BookedMeetingForm prospectId={p.prospectId} customer={p.customer} parallelCallId={p.parallelCallId} compact onDone={p.onMeetingDone} />
        </div>
      )}
      {selected === 'meeting_booked' && p.draft.meetingDone && (
        <p className="text-xs" style={{ color: 'var(--vf-status-booked-fg)' }}>{t('dialer:callPage.meeting.done')}</p>
      )}

      {p.error && (
        <div role="alert" className="rounded-md border p-3 text-sm" style={{ borderColor: 'var(--vf-red)', background: 'var(--vf-red-bg)', color: 'var(--vf-text-1)' }}>
          <p>{t(`dialer:callPage.errors.${p.error.code}`, { defaultValue: t('dialer:callPage.errors.QUALIF_SAVE_FAILED') })}</p>
          <p className="mt-1 flex items-center gap-2 font-mono text-[11px]" style={{ color: 'var(--vf-text-2)' }}>
            {t('dialer:callPage.errors.code', { code: p.error.code, id: p.error.correlationId })}
            <button
              type="button"
              className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
              onClick={() => { void copyText(`${p.error!.code} ${p.error!.correlationId}`).then((ok) => { if (ok) toast.success(t('dialer:callPage.errors.copied')); }); }}
            >
              <Copy className="h-3 w-3" aria-hidden />{t('dialer:callPage.errors.copy')}
            </button>
          </p>
        </div>
      )}

      {qualifying && !countdown && (
        <div className="mt-auto flex flex-col gap-2">
          {p.saveBlockedReason && (
            <p role="note" className="text-xs" style={{ color: 'var(--vf-text-2)' }}>{p.saveBlockedReason}</p>
          )}
          <Button className="min-h-11" disabled={!selected || p.saving || !!p.saveBlockedReason} onClick={() => p.onSave(false)} aria-keyshortcuts="Enter">
            {p.saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />}
            {p.saving ? t('dialer:callPage.saving') : t('dialer:callPage.save')}
          </Button>
          <Button variant="outline" className="min-h-11" disabled={!selected || p.saving || !!p.saveBlockedReason} onClick={() => p.onSave(true)}>
            {t('dialer:callPage.savePause')}
          </Button>
        </div>
      )}

      {countdown && (
        <div className="mt-auto rounded-lg border p-3" style={{ borderColor: 'var(--vf-accent-bd)', background: 'var(--vf-accent-bg)' }}>
          {/* The sentence is announced once; the seconds are not (spec §4.6). */}
          <p role="status" className="text-sm font-medium" style={{ color: 'var(--vf-text-1)' }}>
            {t(!countdown.resumes ? 'dialer:callPage.countdown.done' : countdown.held ? 'dialer:callPage.countdown.held' : 'dialer:callPage.countdown.saved',
              { outcome: t(`dialer:callPage.outcomes.${countdown.outcome}`) })}
          </p>
          {countdown.resumes && !countdown.held && (
            <p aria-hidden className="text-sm tabular-nums" style={{ color: 'var(--vf-text-2)' }}>
              {t('dialer:callPage.countdown.seconds', { seconds: Math.ceil(countdown.remainingMs / 1000) })}
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            {countdown.canUndo && <Button size="sm" variant="outline" className="min-h-11" onClick={p.onUndo} aria-keyshortcuts="U">{t('dialer:callPage.countdown.undo')}</Button>}
            {countdown.resumes && !countdown.held && <Button size="sm" variant="outline" className="min-h-11" onClick={p.onHold} aria-keyshortcuts="Escape">{t('dialer:callPage.countdown.stay')}</Button>}
            {countdown.resumes && <Button size="sm" className="min-h-11" onClick={p.onNext}>{t('dialer:callPage.countdown.now')}</Button>}
          </div>
          {countdown.resumes && !countdown.held && <p className="mt-1 text-[11px]" style={{ color: 'var(--vf-text-2)' }}>{t('dialer:callPage.countdown.extend')}</p>}
        </div>
      )}
    </section>
  );
});
